import { ArrowRight, Calendar, Clock, ImageIcon } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import useNavigateWithGTM from '~/shared/hooks/useNavigateWithGTM'

import styles from '../styles.module.scss'
import { getBlogImageFallbackSrc, getBlogImageSrc } from '../utils/getBlogImageSrc'

export interface BlogPostFrontmatter {
  draft?: boolean
  hidden?: boolean
  homepageFeaturedCaseStudy?: boolean
  homepageFeaturedCaseStudyOrder?: number
  homepageInFocus?: boolean
  homepageTitle?: string
  homepageImage?: string
  homepageImageAlt?: string
  homepageHighlights?: Array<{
    label: string
    text: string
  }>
  slug?: string
  title?: string
  image?: string
  ogImage?: string
  imageAlt?: string
  imageCaption?: string
  imageAttribution?: string
  region?: string
  topic?: string
  category?: string
  readTime?: string
  date?: string
  author?: string
  description?: string
  tags?: string[]
}

interface BlogItemProps {
  slug: string
  frontmatter: BlogPostFrontmatter
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return isNaN(d.getTime())
    ? dateStr
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const BlogItem: React.FC<BlogItemProps> = ({ slug, frontmatter }) => {
  const { navigateWithGTM } = useNavigateWithGTM()
  const { title, image, imageAlt, category, readTime, date, description } = frontmatter
  const imageSrc = getBlogImageSrc(image)
  const fallbackSrc = getBlogImageFallbackSrc(image)
  const [imageError, setImageError] = useState(false)
  const [forceFallback, setForceFallback] = useState(false)
  const showFallback = !imageSrc || imageError
  const formattedDate = formatDate(date)

  useEffect(() => {
    setImageError(false)
    setForceFallback(false)
  }, [imageSrc, fallbackSrc])

  return (
    <article className={styles.blogItem}>
      {/* Trailing slash helps static hosts resolve the prerendered HTML snapshot. */}
      <Link
        to={`/blog/${slug}/`}
        className={styles.blogItemLink}
        onClick={() => navigateWithGTM(`/blog/${slug}/`)}
      >
        <div className={styles.blogItemImageWrapper}>
          {showFallback ? (
            <div className={styles.blogItemImageFallback} aria-hidden>
              <ImageIcon size={48} strokeWidth={1} />
            </div>
          ) : forceFallback && fallbackSrc ? (
            <img
              src={fallbackSrc}
              alt={(imageAlt || title || '').trim() || 'Blog post image'}
              className={styles.blogItemImage}
              onError={() => setImageError(true)}
            />
          ) : fallbackSrc ? (
            <picture>
              <source srcSet={imageSrc} type="image/webp" />
              <img
                src={fallbackSrc}
                alt={(imageAlt || title || '').trim() || 'Blog post image'}
                className={styles.blogItemImage}
                onError={() => setForceFallback(true)}
              />
            </picture>
          ) : (
            <img
              src={imageSrc}
              alt={(imageAlt || title || '').trim() || 'Blog post image'}
              className={styles.blogItemImage}
              onError={() => setImageError(true)}
            />
          )}
        </div>
        <div className={styles.blogItemContent}>
          <div className={styles.blogItemMeta}>
            {category && <span className={styles.blogItemBadge}>{category}</span>}
            {readTime && (
              <span className={styles.blogItemReadTime}>
                <Clock size={14} aria-hidden />
                {readTime}
              </span>
            )}
            {formattedDate && (
              <span className={styles.blogItemDate}>
                <Calendar size={14} aria-hidden />
                {formattedDate}
              </span>
            )}
          </div>
          <h2 className={styles.blogItemTitle}>{title}</h2>
          {description && <p className={styles.blogItemDescription}>{description}</p>}
          <span className={styles.blogItemLearnMore}>
            <ArrowRight size={14} aria-hidden />
            LEARN MORE
          </span>
        </div>
      </Link>
    </article>
  )
}

export default BlogItem
